/**
 * Utility service for handling table column filtering
 */

/**
 * Prepares filter options for columns based on dropdown data
 * @param {Object} dropdownData - Raw dropdown data from API
 * @returns {Object} - Map of column IDs to their filter options
 */
export const prepareFilterOptions = (dropdownData) => {
  if (!dropdownData) return {};

  // Parse the dropdown data if needed
  const parseDropdownData = (data) => {
    // This function should match your existing parseDropdownData implementation
    const companies = [];
    const teams = [];
    const departments = [];
    const divisions = [];
    const positions = [];

    // Process companies
    if (data) {
      Object.entries(data).forEach(([name, company]) => {
        companies.push({ id: company.id, name });

        // Process teams
        if (company.teams) {
          company.teams.forEach((team) => {
            teams.push(team);
          });
        }

        // Process departments and their divisions/positions
        if (company.departments) {
          company.departments.forEach((dept) => {
            departments.push({ id: dept.id, name: dept.name });

            // Add divisions
            if (dept.divisions) {
              dept.divisions.forEach((div) => {
                divisions.push({ id: div.id, name: div.name });

                // Add positions
                if (div.positions) {
                  div.positions.forEach((pos) => {
                    positions.push(pos);
                  });
                }
              });
            }
          });
        }
      });
    }

    return { companies, teams, departments, divisions, positions };
  };

  // If parseDropdownData is already called elsewhere, use the result directly
  const { companies, teams, departments, divisions, positions } =
    typeof dropdownData.companies === "undefined"
      ? parseDropdownData(dropdownData)
      : dropdownData;

  // Map column IDs to their filter options
  return {
    company: companies,
    team: teams,
    department: departments,
    division: divisions,
    position: positions,
    // Add rating options separately since they're static
    rating: [
      { id: "", label: "Без рейтинга" },
      { id: "1", label: "1 Звезда" },
      { id: "2", label: "2 Звезды" },
      { id: "3", label: "3 Звезды" },
      { id: "4", label: "4 Звезды" },
      { id: "5", label: "5 Звезд" },
    ],
  };
};

/**
 * Handles filter changes and updates filter state
 * @param {string} columnId - ID of the column being filtered
 * @param {*} value - New filter value
 * @param {boolean} isApply - Whether this is an apply action
 * @param {Object} currentFilters - Current filter state
 * @param {Function} updateFunction - Function to update filter state
 * @param {Object} setterFunctions - Map of column IDs to their setter functions
 * @returns {Boolean} - Whether the filter was applied successfully
 */
export const handleFilterChange = (
  columnId,
  value,
  isApply,
  currentFilters,
  updateFunction,
  setterFunctions
) => {
  console.log(`Filter change for ${columnId}:`, { value, isApply });

  // Map column IDs to their corresponding filter parameters
  const filterMapping = {
    company: "company",
    team: "team",
    department: "department",
    position: "position",
    rating: "rating",
    // Add any other columns that need filter mapping
  };

  // Get the actual filter key to update
  const filterKey = filterMapping[columnId];

  if (!filterKey) {
    console.warn(`No filter mapping found for column ${columnId}`);
    return;
  }

  // Handle different actions
  if (isApply) {
    // company
    if (setterFunctions?.setSelectedCompanies && filterKey === "company") {
      const selectedValues = value || [];
      setterFunctions.setSelectedCompanies(selectedValues);
      updateFunction?.({ [filterKey]: selectedValues.join(",") });

      // team
    } else if (setterFunctions?.setSelectedTeams && filterKey === "team") {
      const selectedValues = value || [];
      setterFunctions.setSelectedTeams(selectedValues);
      updateFunction?.({ [filterKey]: selectedValues.join(",") });

      // department
    } else if (
      setterFunctions?.setSelectedDepartments &&
      filterKey === "department"
    ) {
      const selectedValues = value || [];
      setterFunctions.setSelectedDepartments(selectedValues);
      updateFunction?.({ [filterKey]: selectedValues.join(",") });

      // position
    } else if (
      setterFunctions?.setSelectedPositions &&
      filterKey === "position"
    ) {
      const selectedValues = value || [];
      setterFunctions.setSelectedPositions(selectedValues);
      updateFunction?.({ [filterKey]: selectedValues.join(",") });

      // rating
    } else if (setterFunctions?.setSelectedRating && filterKey === "rating") {
      const selectedValues = value || [];
      setterFunctions.setSelectedRating(selectedValues);
      updateFunction?.({ [filterKey]: selectedValues.join(",") });

      // fallback
    } else if (updateFunction) {
      const payload = Array.isArray(value) ? value.join(",") : value;
      updateFunction({ [filterKey]: payload });
    }

    console.log(`Applied filter for ${filterKey}:`, value);
    return true;
  }

  return false;
};

/**
 * Get selected values for a specific column from the current filters
 * @param {string} columnId - ID of the column
 * @param {Object} currentFilters - Current filter state
 * @returns {Array} Array of selected values
 */
export const getSelectedValuesForColumn = (columnId, currentFilters) => {
  if (!currentFilters) return [];

  switch (columnId) {
    case "company":
      return currentFilters.company
        ? currentFilters.company.split(",").filter(Boolean)
        : [];
    case "team":
      return currentFilters.team
        ? currentFilters.team.split(",").filter(Boolean)
        : [];
    case "department":
      return currentFilters.department
        ? currentFilters.department.split(",").filter(Boolean)
        : [];
    case "position":
      return currentFilters.position
        ? currentFilters.position.split(",").filter(Boolean)
        : [];
    case "rating":
      return currentFilters.rating
        ? currentFilters.rating.split(",").filter(Boolean)
        : [];
    default:
      return [];
  }
};
